import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { useAppSelector } from '@/redux/Store';
import { getMediaUrl } from '@/constants/media';
import { formatName } from '@/utils/formatName';
import { profileLink } from '@/utils/profileLink';
import './LeaderboardPreview.scss';

// Match the per-profile rep card + the /leaderboards `reputation`
// metric formatter so the same KlyxValue reads identically wherever
// it shows up. 2 decimals, no unit (KlyxValue is dimensionless).
const formatReputation = (v: number) =>
  v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  value: number;
  metric: string;
}

interface ApiResponse {
  scope: 'global' | 'home' | 'touring';
  scopeArea: { countryCode: string; admin1: string | null } | null;
  availableScopes: ('global' | 'home' | 'touring')[];
  entries: LeaderboardEntry[];
  self: LeaderboardEntry | null;
}

/**
 * Compact 5-row Most Reputable preview for the dashboard. Ranks by
 * KlyxValue — the ADR-008 durable-reputation signal that anchors
 * the /leaderboards page. Defaults to the caller's home location
 * when one is set (server falls back to global silently otherwise —
 * same UX rules as /leaderboards).
 *
 * Highlights the caller's row if visible. Tapping anywhere in the
 * preview lands on /leaderboards/most-reputable with the same scope
 * already selected so the full board picks up where this preview
 * left off.
 */
const LeaderboardPreview: React.FC = () => {
  const currentUser = useAppSelector((s) => s.auth?.user);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Try home first when signed in; the server downgrades to
        // global if the caller has no home location set. Anonymous
        // users go straight to global.
        const scope = currentUser ? 'home' : 'global';
        const res = await api.get<ApiResponse>('/leaderboards/most-reputable', {
          params: { limit: 5, scope },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  if (loading) {
    return (
      <section className="leaderboard-preview">
        <header className="leaderboard-preview__header">
          <h2>Most Reputable</h2>
        </header>
        <p className="leaderboard-preview__hint">Loading…</p>
      </section>
    );
  }

  if (!data || data.entries.length === 0) {
    return null;
  }

  const fullBoardHref = data.scope === 'global'
    ? '/leaderboards/most-reputable'
    : `/leaderboards/most-reputable?scope=${data.scope}`;

  const areaLabel = data.scopeArea
    ? [data.scopeArea.admin1, data.scopeArea.countryCode].filter(Boolean).join(', ')
    : 'Global';

  return (
    <section className="leaderboard-preview">
      <header className="leaderboard-preview__header">
        <div>
          <h2>Most Reputable</h2>
          <span className="leaderboard-preview__area">Showing: {areaLabel}</span>
        </div>
        <Link to={fullBoardHref} className="leaderboard-preview__view-all">View all →</Link>
      </header>
      <ol className="leaderboard-preview__list">
        {data.entries.map((entry) => (
          <li
            key={entry.userId}
            className={`leaderboard-preview__row ${entry.userId === currentUser?.id ? 'is-me' : ''}`}
          >
            <span className={`leaderboard-preview__rank rank-${Math.min(entry.rank, 4)}`}>
              #{entry.rank}
            </span>
            <Link
              to={profileLink(entry.username, 'leaderboard')}
              className="leaderboard-preview__avatar"
              aria-label={`View ${entry.displayName}'s profile`}
            >
              {entry.avatarUrl ? (
                <img src={getMediaUrl(entry.avatarUrl) ?? undefined} alt={entry.displayName} />
              ) : (
                <span className="leaderboard-preview__avatar-placeholder">
                  {entry.displayName[0]?.toUpperCase() ?? '?'}
                </span>
              )}
            </Link>
            <div className="leaderboard-preview__identity">
              <Link to={profileLink(entry.username, 'leaderboard')} className="leaderboard-preview__name">
                {formatName({ displayName: entry.displayName, username: entry.username, limit: 18 })}
              </Link>
            </div>
            <span className="leaderboard-preview__value">{formatReputation(entry.value)}</span>
          </li>
        ))}
        {data.self && (
          <>
            <li className="leaderboard-preview__separator" aria-hidden="true">·  ·  ·</li>
            <li className="leaderboard-preview__row is-me">
              <span className="leaderboard-preview__rank">#{data.self.rank}</span>
              <Link
                to={profileLink(data.self.username, 'leaderboard')}
                className="leaderboard-preview__avatar"
              >
                {data.self.avatarUrl ? (
                  <img src={getMediaUrl(data.self.avatarUrl) ?? undefined} alt={data.self.displayName} />
                ) : (
                  <span className="leaderboard-preview__avatar-placeholder">
                    {data.self.displayName[0]?.toUpperCase() ?? '?'}
                  </span>
                )}
              </Link>
              <div className="leaderboard-preview__identity">
                <span className="leaderboard-preview__name">You</span>
              </div>
              <span className="leaderboard-preview__value">{formatReputation(data.self.value)}</span>
            </li>
          </>
        )}
      </ol>
    </section>
  );
};

export default LeaderboardPreview;
